import React from "react";
import { FaArrowUp, FaArrowDown, FaDollarSign, FaServer, FaTags, FaBell } from "react-icons/fa";

const TopLayerBoxes = ({ reportData, loading }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const parseBillingTypes = (billingTypesStr) => {
    if (!billingTypesStr) return { PAYG: 0, RESERVATION: 0 };
    try {
      const parsed = JSON.parse(billingTypesStr);
      const result = { PAYG: 0, RESERVATION: 0 };
      parsed.forEach(item => {
        if (item.type === "PAYG") result.PAYG = item.count;
        if (item.type === "RESERVATION") result.RESERVATION = item.count;
      });
      return result;
    } catch {
      return { PAYG: 0, RESERVATION: 0 };
    }
  };

  const billingData = parseBillingTypes(reportData?.billingTypes);
  const isPositiveChange = reportData?.invoicePercentChange > 0;
  const percentChangeValue = Math.abs(reportData?.invoicePercentChange || 0);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-primary-dark rounded-xl p-4 sm:p-6 border border-gray-700/50 animate-pulse">
            <div className="h-20 sm:h-24"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
      <div className="bg-primary-dark rounded-xl p-4 sm:p-6 border border-gray-700/50 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary-cyan/20">
        <div className="flex justify-between items-start mb-3 sm:mb-4">
          <h3 className="text-gray-300 text-xs sm:text-base font-semibold uppercase tracking-wide">Total Revenue</h3>
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary-cyan/20 flex items-center justify-center">
            <FaDollarSign className="text-primary-cyan text-xl sm:text-2xl" />
          </div>
        </div>
        <div className="mb-2 sm:mb-3">
          <span className="text-white text-2xl sm:text-4xl font-extrabold tracking-tight">{formatCurrency(reportData?.lastMonthInvoiceValue)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-1 sm:gap-2">
          {isPositiveChange ? (
            <>
              <span className="text-green-400 text-xs sm:text-base font-semibold flex items-center gap-1">
                <FaArrowUp className="text-green-400 text-xs sm:text-sm" />
                +{percentChangeValue}%
              </span>
              <span className="text-gray-400 text-xs sm:text-sm font-medium">from last month</span>
            </>
          ) : (
            <>
              <span className="text-red-400 text-xs sm:text-base font-semibold flex items-center gap-1">
                <FaArrowDown className="text-red-400 text-xs sm:text-sm" />
                -{percentChangeValue}%
              </span>
              <span className="text-gray-400 text-xs sm:text-sm font-medium">from last month</span>
            </>
          )}
        </div>
      </div>

      <div className="bg-primary-dark rounded-xl p-4 sm:p-6 border border-gray-700/50 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary-cyan/20">
        <div className="flex justify-between items-start mb-3 sm:mb-4">
          <h3 className="text-gray-300 text-xs sm:text-base font-semibold uppercase tracking-wide">Total VMs</h3>
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary-cyan/20 flex items-center justify-center">
            <FaServer className="text-primary-cyan text-xl sm:text-2xl" />
          </div>
        </div>
        <div className="mb-2 sm:mb-3">
          <span className="text-white text-3xl sm:text-5xl font-extrabold tracking-tight">{reportData?.totalVMs || 0}</span>
        </div>
        <div className="text-gray-400 text-xs sm:text-sm font-medium">Active Virtual Machines</div>
      </div>

      <div className="bg-primary-dark rounded-xl p-4 sm:p-6 border border-gray-700/50 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary-cyan/20">
        <div className="flex justify-between items-start mb-3 sm:mb-4">
          <h3 className="text-gray-300 text-xs sm:text-base font-semibold uppercase tracking-wide">Billing Types</h3>
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary-cyan/20 flex items-center justify-center">
            <FaTags className="text-primary-cyan text-xl sm:text-2xl" />
          </div>
        </div>
        <div className="space-y-2 sm:space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-400 text-sm sm:text-base font-medium">PAYG</span>
            <span className="text-white text-xl sm:text-2xl font-bold">{billingData.PAYG}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-400 text-sm sm:text-base font-medium">Reservation</span>
            <span className="text-white text-xl sm:text-2xl font-bold">{billingData.RESERVATION}</span>
          </div>
        </div>
      </div>

      <div className="bg-primary-dark rounded-xl p-4 sm:p-6 border border-gray-700/50 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary-cyan/20">
        <div className="flex justify-between items-start mb-3 sm:mb-4">
          <h3 className="text-gray-300 text-xs sm:text-base font-semibold uppercase tracking-wide">Total Alerts</h3>
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary-cyan/20 flex items-center justify-center">
            <FaBell className="text-primary-cyan text-xl sm:text-2xl" />
          </div>
        </div>
        <div className="mb-2 sm:mb-3">
          <span className="text-white text-3xl sm:text-5xl font-extrabold tracking-tight">{reportData?.totalAlerts || 0}</span>
        </div>
        <div className="text-gray-400 text-xs sm:text-sm font-medium">Active Alerts</div>
      </div>
    </div>
  );
};

export default TopLayerBoxes;